/**
 * M2 onboarding: Step 2 (Verify) — EIN block, website enrichment (logo + brand color), Create Organization.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useClerk } from '@clerk/clerk-react';
import OnboardingStepLayout from '../shared/OnboardingStepLayout';
import OrgNameAndEIN from './OrgNameAndEIN';
import OrgInformation from './OrgInformation';
import OrgBrandSection from './OrgBrandSection';
import {
  callCreateOrg,
  callWebsiteEnrich,
  normalizeWebsiteUrl,
  validateEIN,
  validateUrl,
} from './orgSetupUtils';
import {
  buildCreateOrgPayload,
  getCreateOrgDisplayName,
  getCreateOrgLogoSource,
} from './orgSetupEntityScope';
import { ENTITY_TYPE_INDIVIDUAL } from '../shared/useOnboardingState';
import { clearPlanIntent } from '../shared/planIntent';
import { activateDashboardOrganization } from '../../../utils/activeOrganization';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ImageUploader from '../../uploads/ImageUploader';
import OnboardingCreatingLoading from '../../OnboardingCreatingLoading';
import OnboardingLoadingPattern from '../../OnboardingLoadingPattern';
import { uploadImage } from '../../../../api/images/uploadImage';
import { persistOnboardingLogoVersion } from '../../../../api/images/persistImageVersion';
import { safeCapture } from '../../../utils/analytics';
import { BACK_TO_DASHBOARD_LABEL } from './onboardingCopy';
import OrgNameTakenNotice from './OrgNameTakenNotice';
import { extractCreateOrgErrorBody, ORG_NAME_TAKEN_CODE } from './orgSetupErrors';

export default function OrgSetupStep2({
  state,
  update,
  setStep,
  onBack,
  onCancel,
  onComplete,
  organizationActivationRedirectUrl,
  getAuthToken,
  posthog,
  clerkUser,
  step = 2,
  totalSteps = 2,
}) {
  const clerk = useClerk();
  const [error, setError] = useState('');
  // Backend error code for a rejection that gets its own UI (e.g. ORG_NAME_TAKEN).
  const [errorCode, setErrorCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [showLogoUploadModal, setShowLogoUploadModal] = useState(false);
  /** Preview URL for ImageUploader while cropping (object URL or remote URL) */
  const [logoUploadPreview, setLogoUploadPreview] = useState(null);

  const stateRef = useRef(state);
  stateRef.current = state;
  const websiteEnrichDebounceRef = useRef(null);
  const websiteEnrichRequestIdRef = useRef(0);

  // UI-001: 5-second timeout for website enrichment loading state.
  // If enrichment hasn't resolved after 5s, force fallback to editable defaults.
  const enrichTimeoutRef = useRef(null);
  const isEnrichLoading = state.hasWebsite && state.websiteEnrichStatus === 'loading';

  useEffect(() => {
    if (!isEnrichLoading) {
      if (enrichTimeoutRef.current) {
        clearTimeout(enrichTimeoutRef.current);
        enrichTimeoutRef.current = null;
      }
      return;
    }
    enrichTimeoutRef.current = setTimeout(() => {
      console.log('[Onboarding Step 2] Enrichment timeout (5s) — falling back to defaults');
      update({ websiteEnrichStatus: 'error' });
    }, 5000);
    return () => {
      if (enrichTimeoutRef.current) {
        clearTimeout(enrichTimeoutRef.current);
        enrichTimeoutRef.current = null;
      }
    };
  }, [isEnrichLoading, update]);

  // Debug: log state updates to verify data flows through
  useEffect(() => {
    console.log('[Onboarding Step 2] State updated', {
      orgName: state.orgName,
      description: state.description,
      logoUrl: state.logoUrl,
      brandColor: state.brandColor,
      einLookupStatus: state.einLookupStatus,
    });
  }, [state.orgName, state.description, state.logoUrl, state.brandColor, state.einLookupStatus]);

  useEffect(
    () => () => {
      if (websiteEnrichDebounceRef.current) {
        clearTimeout(websiteEnrichDebounceRef.current);
        websiteEnrichDebounceRef.current = null;
      }
    },
    [],
  );

  /** Debounced website-enrich when user enters a valid URL (same API as Step 1). */
  const handleWebsiteUrlChange = useCallback(
    (raw) => {
      const value = typeof raw === 'string' ? raw : '';
      update({ websiteUrl: value, hasWebsite: !!value.trim() });

      if (websiteEnrichDebounceRef.current) {
        clearTimeout(websiteEnrichDebounceRef.current);
        websiteEnrichDebounceRef.current = null;
      }

      const trimmed = value.trim();
      if (!trimmed) {
        websiteEnrichRequestIdRef.current += 1;
        update({ websiteEnrichStatus: 'idle' });
        return;
      }

      if (!validateUrl(trimmed)) {
        return;
      }

      const url = normalizeWebsiteUrl(trimmed);
      websiteEnrichDebounceRef.current = window.setTimeout(() => {
        websiteEnrichDebounceRef.current = null;
        const reqId = (websiteEnrichRequestIdRef.current += 1);
        safeCapture(posthog, 'onboarding_website_enrichment_attempted', {
          step: 2,
          step_name: 'org_setup_step_2',
          url,
        });
        update({ websiteEnrichStatus: 'loading' });
        callWebsiteEnrich({ url })
          .then((res) => {
            if (reqId !== websiteEnrichRequestIdRef.current) return;
            if (res && res.ok && res.data) {
              const d = res.data;
              const prevDesc = stateRef.current.description || '';
              const finalDesc = (d.description || d.ogDescription || prevDesc || '').slice(0, 1100);
              update({
                websiteEnrichStatus: 'ok',
                ogImage: d.ogImage || '',
                favicon: d.favicon || '',
                suggestedBrandColor: d.brandColor || '',
                description: finalDesc,
              });
              safeCapture(posthog, 'onboarding_website_enrichment_succeeded', {
                step: 2,
                step_name: 'org_setup_step_2',
                fields_extracted: ['ogImage', 'brandColor', 'description'].filter((k) => d[k]),
              });
            } else {
              update({ websiteEnrichStatus: 'error' });
            }
          })
          .catch(() => {
            if (reqId !== websiteEnrichRequestIdRef.current) return;
            update({ websiteEnrichStatus: 'error' });
          });
      }, 500);
    },
    [update, posthog],
  );

  const handleLogoUploadModalChange = (open) => {
    setShowLogoUploadModal(open);
    if (!open) setLogoUploadPreview(null);
  };

  const handleCancel = useCallback(() => {
    // Do not let an in-flight request restore `loading` after this scoped
    // draft has been saved and the user returns to their dashboard.
    websiteEnrichRequestIdRef.current += 1;
    if (websiteEnrichDebounceRef.current) {
      clearTimeout(websiteEnrichDebounceRef.current);
      websiteEnrichDebounceRef.current = null;
    }
    if (enrichTimeoutRef.current) {
      clearTimeout(enrichTimeoutRef.current);
      enrichTimeoutRef.current = null;
    }
    if (stateRef.current.websiteEnrichStatus === 'loading') {
      update({ websiteEnrichStatus: 'idle' });
    }
    onCancel?.();
  }, [onCancel, update]);

  const handleCreateOrg = async () => {
    setError('');
    setErrorCode('');
    const isIndividual = state.entityType === ENTITY_TYPE_INDIVIDUAL;
    const displayName = getCreateOrgDisplayName(state);
    if (!displayName) {
      setError(isIndividual
        ? 'Please enter a name for your fundraiser (Step 1).'
        : 'Please enter an organization name (Step 1).');
      return;
    }
    if (!isIndividual && !validateEIN(state.ein)) {
      setError('A valid 9-digit EIN is required for nonprofit organizations. Please enter your EIN below or go back to Step 1.');
      return;
    }
    setCreating(true);
    const activationPath = state.hasWebsite ? 'A' : 'B';
    console.log('[Onboarding] Step 2 create org', {
      displayName,
      ein: state.ein,
      hasWebsite: state.hasWebsite,
      websiteUrl: state.websiteUrl,
      activationPath,
    });
    // Determine logo source and whether it's base64 (user upload) vs remote URL (EIN/enrichment).
    // Base64 logos (~100KB-4MB) exceed DynamoDB's 400KB item limit, so they must be uploaded to S3
    // after org creation. Remote URLs are small strings and safe to store directly.
    const logoSource = getCreateOrgLogoSource(state);
    const isBase64Logo = logoSource.startsWith('data:');

    const payload = buildCreateOrgPayload(state);
    try {
      const result = await callCreateOrg(payload, {
        idempotencyKey: state.creationRequestId,
      });
      const data = result?.data || result;
      if (result?.ok && data && (data.organizationId || data.clerkOrgId)) {
        console.log('[Onboarding] Step 2 create org success, setting active org', data.clerkOrgId);
        // The plan intent has been sent to create-org (stored as
        // onboardingDetails.selectedPlan); clear the local stash so a later
        // unrelated org creation or a returning user can't re-apply a stale choice.
        clearPlanIntent();
        if (data.warnings?.length) {
          console.warn('[Onboarding] Backend warnings:', data.warnings);
        }
        safeCapture(posthog, 'onboarding_org_created', {
          org_id: data.organizationId,
          activationPath,
          activation_path: activationPath,
          isTaxExempt: state.isTaxExempt,
          hasWebsite: state.hasWebsite,
        });
        const clerkOrgId = data.clerkOrgId;
        try {
          const { syncResult } = await activateDashboardOrganization({
            clerk,
            clerkOrgId,
            syncContext: 'Syncing active organization after provisioning',
            // Additional-organization creation switches Clerk tenants while this
            // route is mounted. Let Clerk navigate as part of setActive so the
            // wizard cannot remount at persisted step 2 before a later redirect.
            redirectUrl: organizationActivationRedirectUrl,
          });
          if (!syncResult?.ok) {
            console.warn('[Onboarding] active-org sync failed after provisioning', syncResult?.status);
          }
          // When Clerk isn't about to navigate the document itself (no redirectUrl — the
          // first-time onboarding path), refresh the in-memory Clerk user before handing off
          // to the dashboard. Without this, clerkUser.organizationMemberships and
          // publicMetadata.signupComplete are still stale for a render or two after
          // setActive(), and ClerkAuthWrapper's onboarding gate reads that stale state and
          // redirects straight back to /onboarding (bug: create-org succeeds, dashboard
          // loading screen flashes, then bounces to Step 2). Best-effort — the gate also
          // has a session-claim fallback now, so a reload failure (or timeout) here is
          // non-fatal. Bounded with a timeout so a slow/hung Clerk API can't strand the
          // user on the "Creating…" screen — the org was already created either way.
          if (!organizationActivationRedirectUrl && clerkUser) {
            try {
              await Promise.race([
                clerkUser.reload(),
                new Promise((_, reject) =>
                  setTimeout(() => reject(new Error('clerkUser.reload() timed out')), 5000),
                ),
              ]);
            } catch (reloadError) {
              console.warn('[Onboarding] clerkUser.reload() after provisioning failed (non-blocking)', reloadError);
            }
          }
        } catch (activationError) {
          console.error('[Onboarding] organization was created but could not be activated', activationError);
          setCreating(false);
          setError(
            'Your organization was created, but we could not open it yet. Please try again.',
          );
          return;
        }
        // Upload base64 logo to S3 (fire-and-forget — don't block onboarding completion).
        // Remote URL logos (from EIN lookup or website enrichment) are stored in the org record directly.
        if (isBase64Logo && logoSource && displayName) {
          const token = await getAuthToken();
          if (token) {
            uploadImage({
              organizationId: data.organizationId,
              imageData: logoSource,
              imageName: `${displayName}_Logo`,
              imageType: 'logo',
              nonprofitName: displayName,
              jwtToken: token,
            }).then(async (uploadResult) => {
              if (uploadResult.success && uploadResult.imageVersion) {
                await persistOnboardingLogoVersion({
                  imageVersion: uploadResult.imageVersion,
                  jwtToken: token,
                });
                console.log('[Onboarding] Logo uploaded to S3', uploadResult.imageUrl);
              } else {
                console.warn('[Onboarding] Logo S3 upload failed (non-blocking)', uploadResult.error);
              }
            }).catch((err) => {
              console.warn('[Onboarding] Logo S3 upload error (non-blocking)', err);
            });
          }
        }
        onComplete?.({ ...data, merchantName: displayName });
        return;
      }
      setCreating(false);
      // Show contextual error messages based on failure type
      const status = result?.status;
      // The backend returns 400 with a specific, user-fixable reason for a
      // validation rejection (e.g. an org name that is a URL — Clerk forbids it).
      // This is NOT retryable, so surface the exact message and do NOT tell the
      // user to "wait and try again" (which only fits the transient 5xx path).
      //
      // safeFetchJSON wraps a non-2xx body as `error: "HTTP 400: {json}"` (data is null),
      // so the body is parsed by extractCreateOrgErrorBody (see orgSetupErrors.js).
      const { error: backendError, code: backendCode } = extractCreateOrgErrorBody(result);
      let msg;
      if (status === 400 && backendCode === ORG_NAME_TAKEN_CODE) {
        setErrorCode(ORG_NAME_TAKEN_CODE);
        setError('');
        return;
      }
      if (status === 400 && backendError) {
        msg = backendError;
      } else if (status === 409) {
        msg = 'This organization is still being created. Please wait a moment and try again.';
      } else if (status === 502 || status === 503) {
        msg = 'Our servers are temporarily unavailable. Please wait a moment and try again.';
      } else if (status === 429) {
        msg = 'Too many requests. Please wait a moment before trying again.';
      } else if (result?.isNetworkError) {
        msg = 'Please check your internet connection and try again.';
      } else {
        msg = backendError || 'Could not create organization. Please try again.';
      }
      setError(msg);
    } catch (e) {
      console.warn('[Onboarding] Step 2 create org error', e);
      setCreating(false);
      const isNetwork = e?.message?.includes('Failed to fetch') || e?.message?.includes('Load failed');
      setError(
        isNetwork
          ? 'Please check your internet connection and try again.'
          : (e?.message || 'Something went wrong. Please try again.'),
      );
    }
  };

  /** Sign out (else Clerk bounces back into onboarding) and land on the sign-in form. */
  const handleSignInDifferentAccount = useCallback(async () => {
    const signInPath = window.location.pathname.startsWith('/dashboard')
      ? '/dashboard/signin'
      : '/signin';
    try {
      await clerk.signOut({ redirectUrl: `${window.location.origin}${signInPath}` });
    } catch (signOutError) {
      // Do not navigate: the session is still live and /signin would bounce the
      // user straight back into onboarding. Keep them here with a visible error.
      console.error('[Onboarding] sign out failed', signOutError);
      setError('We could not sign you out. Please try again.');
    }
  }, [clerk]);

  /** Back to Step 1 to rename; drop the name-taken state so it cannot linger. */
  const handleChangeName = useCallback(() => {
    setErrorCode('');
    setError('');
    onBack?.();
  }, [onBack]);

  // UI-001: Block step 2 render while website enrichment is in-flight (Path A only).
  // Shows a loading screen instead of stale/default content.
  if (isEnrichLoading) {
    return (
      <OnboardingStepLayout
        showProgressBar={false}
        step={step}
        totalSteps={totalSteps}
        title="Verify & create"
        onBack={onBack}
        error=""
        ctaLabel="Create Account"
        onCta={() => {}}
        ctaDisabled
        secondaryActionLabel={onCancel ? BACK_TO_DASHBOARD_LABEL : undefined}
        onSecondaryAction={onCancel ? handleCancel : undefined}
      >
        <div className="flex flex-col items-center justify-center py-10 sm:py-16">
          <OnboardingLoadingPattern
            title="Generating your organization profile"
            messages={[
              "We're pulling details from your website to save you time.",
              'Gathering your logo, colors, and description…',
            ]}
            messageIntervalMs={2800}
          />
        </div>
      </OnboardingStepLayout>
    );
  }

  if (creating) {
    return <OnboardingCreatingLoading />;
  }

  return (
    <>
      <OnboardingStepLayout
        showProgressBar={false}
        step={step}
        totalSteps={totalSteps}
        title="Verify & create"
        onBack={onBack}
        error={error}
        notice={
          errorCode === ORG_NAME_TAKEN_CODE ? (
            <OrgNameTakenNotice
              orgName={getCreateOrgDisplayName(state)}
              onChangeName={onBack ? handleChangeName : undefined}
              onSignInDifferent={handleSignInDifferentAccount}
            />
          ) : null
        }
        ctaLabel={creating ? 'Creating…' : 'Create Account'}
        onCta={handleCreateOrg}
        ctaDisabled={creating || errorCode === ORG_NAME_TAKEN_CODE}
        secondaryActionLabel={onCancel ? BACK_TO_DASHBOARD_LABEL : undefined}
        onSecondaryAction={onCancel ? handleCancel : undefined}
      >
        <div className="space-y-6 [--edge:var(--warm-brown)]">
          <p className="text-sm text-ink-secondary">Please verify the information below to create your CharityStack account.</p>
          <div className="space-y-6">
            {/* Nonprofit: EIN verification block */}
            {state.entityType !== ENTITY_TYPE_INDIVIDUAL && (
              <OrgNameAndEIN
                status={state.einLookupStatus}
                payload={state.einLookupPayload}
                ein={state.ein}
                legalName={state.legalName}
                onEINChange={(v) => update({ ein: v })}
                onLegalNameChange={(v) => update({ legalName: v })}
              />
            )}

            {/* Info + brand sections — always visible.
                OrgInformation uses pencil pattern only for API-prefilled fields;
                regular inputs otherwise. */}
            <OrgInformation
              payload={state.einLookupPayload}
              description={state.description}
              descriptionLoading={state.websiteEnrichStatus === 'loading'}
              onDescriptionChange={(v) => update({ description: v })}
              // Only show website field on Step 2 when the user provided one in Step 1 (Path A).
              // Path B users decided they don't have a website — no input here.
              websiteUrl={state.hasWebsite ? state.websiteUrl : undefined}
              onWebsiteUrlChange={state.hasWebsite ? handleWebsiteUrlChange : undefined}
            />
            <OrgBrandSection
              payload={state.einLookupPayload}
              logoUrl={state.logoUrl}
              onLogoUrlChange={(v) => update({ logoUrl: v })}
              userUploadedLogoUrl={state.userUploadedLogoUrl}
              ogImage={state.ogImage}
              onUploadClick={() => {
                console.log('[Onboarding] Opening logo upload modal');
                setShowLogoUploadModal(true);
              }}
              suggestedBrandColor={state.suggestedBrandColor}
              brandColor={state.brandColor}
              onBrandColorChange={(v) => update({ brandColor: v })}
            />
          </div>
        </div>
      </OnboardingStepLayout>

      <Dialog open={showLogoUploadModal} onOpenChange={handleLogoUploadModalChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload your logo</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-ink-muted mb-2">
            Choose an image file. You can crop it before it&apos;s applied to your organization.
          </p>
          <ImageUploader
            image_type="logo"
            desiredHeight={300}
            desiredWidth={600}
            image={logoUploadPreview}
            setImage={setLogoUploadPreview}
            setResizedImage={(base64) => {
              if (base64) {
                update({ logoUrl: base64, userUploadedLogoUrl: base64 });
                setShowLogoUploadModal(false);
                setLogoUploadPreview(null);
                console.log('[Onboarding] Logo saved from upload (base64 length)', base64.length);
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
